




























































































































































import { Vue, Component } from 'vue-property-decorator';
import { VbSnackbarService } from 'bag-of-holding-library';
import AgreementLimit from '@/domain/agreement-limits/entity/agreement-limit.entity';
import AgreementType from '@/domain/agreement-limits/entity/agreement-type.enum';
import AgreementLimitService from '@/domain/agreement-limits/service/agreement-limit.service';
import ModalWarning from '@/shared/modal/modal-warning.component.vue';
import ModalOptions from '@/shared/modal/modal-options.entity';
import CurrencyField from '@/shared/currency-field.component.vue';
import LabelSlot from '@/shared/slots/label-slot.component.vue';
import Rules from '@/shared/rules/rules';
import AgreementLimitHistoryDrawer from './agreement-limit-history-drawer.component.vue';

interface AgreementTypeOption {
  key: AgreementType;
  label: string;
}

interface CargoSectionOption {
  leader: boolean;
  label: string;
  tooltip: string;
}

interface LimitFieldOption {
  key: string;
  label: string;
  kind: 'percentage' | 'currency';
}

const AGREEMENT_TYPES: AgreementTypeOption[] = [
  { key: AgreementType.INITIAL_SCORE, label: 'Acordos Iniciais' },
  { key: AgreementType.COUNTING, label: 'Contagens' },
  { key: AgreementType.UNSCHEDULED_SCORE, label: 'Acordos Avulsos' },
];

const CARGO_SECTIONS: CargoSectionOption[] = [
  {
    leader: true,
    label: 'Cargos de Liderança',
    tooltip: 'Determinado pela flag de Liderança configurada em Cadastros → Cargos '
      + '(RoleType.MANAGER/SHAREHOLDER).',
  },
  {
    leader: false,
    label: 'Colaboradores',
    tooltip: 'Determinado pela ausência da flag de Liderança em Cadastros → Cargos '
      + '(RoleType.DEFAULT).',
  },
];

const LIMIT_FIELDS: LimitFieldOption[] = [
  { key: 'duczSyndicateFreePercentage', label: 'DUCZ Livre', kind: 'percentage' },
  { key: 'duczFreeMinimumValue', label: 'DUCZ Livre mínimo', kind: 'currency' },
  { key: 'duczFoodMinimumValue', label: 'DUCZ Alimentação mínimo', kind: 'currency' },
  { key: 'duczFoodMaximumValue', label: 'DUCZ Alimentação máximo', kind: 'currency' },
  { key: 'benefitsSumPercentage', label: 'Somatório de Benefícios', kind: 'percentage' },
  { key: 'totalReadjustmentPercentage', label: 'Reajuste Total', kind: 'percentage' },
];

const MIN_MAX_FIELD_KEYS = ['duczFoodMinimumValue', 'duczFoodMaximumValue'];

@Component({
  components: {
    ModalWarning,
    CurrencyField,
    LabelSlot,
    AgreementLimitHistoryDrawer,
  },
})
export default class AgreementLimitsView extends Vue {
  private originalLimits: AgreementLimit[] = [];

  private draftLimits: AgreementLimit[] = [];

  private editMode: boolean = false;

  private showEditWarning: boolean = false;

  private showSaveConfirmation: boolean = false;

  private showCancelConfirmation: boolean = false;

  private showHistoryDrawer: boolean = false;

  private saving: boolean = false;

  private activeTab: number = 0;

  private loading: boolean = false;

  private accessDenied: boolean = false;

  private agreementTypes: AgreementTypeOption[] = AGREEMENT_TYPES;

  private cargoSections: CargoSectionOption[] = CARGO_SECTIONS;

  private limitFields: LimitFieldOption[] = LIMIT_FIELDS;

  private rules: Rules = new Rules();

  private crossFieldErrors: Record<string, string> = {};

  private editWarningOptions: ModalOptions = {
    title: 'Editar Limites de Remuneração',
    description: 'Você está prestes a habilitar a edição dos 6 conjuntos de limites '
      + '(Acordos Iniciais, Contagens e Acordos Avulsos, para Cargos de Liderança e '
      + 'Colaboradores). Deseja continuar?',
    labelCancel: 'Cancelar',
    labelOk: 'Continuar',
    okColor: 'primary',
    showCancel: true,
    showOk: true,
    width: 500,
  };

  private saveWarningOptions: ModalOptions = {
    title: 'Salvar Limites de Remuneração',
    description: 'Você está prestes a persistir os 6 conjuntos de limites de remuneração '
      + '(Acordos Iniciais, Contagens e Acordos Avulsos, para Cargos de Liderança e '
      + 'Colaboradores). Deseja confirmar o salvamento?',
    labelCancel: 'Cancelar',
    labelOk: 'Confirmar',
    okColor: 'primary',
    showCancel: true,
    showOk: true,
    width: 500,
  };

  private cancelWarningOptions: ModalOptions = {
    title: 'Cancelar Edição de Limites de Remuneração',
    description: 'Você está prestes a descartar as alterações realizadas em todas as 3 abas '
      + '(Acordos Iniciais, Contagens e Acordos Avulsos) — não apenas na aba atual. '
      + 'Deseja continuar?',
    labelCancel: 'Voltar',
    labelOk: 'Descartar',
    okColor: 'primary',
    showCancel: true,
    showOk: true,
    width: 500,
  };

  get activeLimits(): AgreementLimit[] {
    return this.editMode ? this.draftLimits : this.originalLimits;
  }

  getLimit(agreementType: AgreementType, leader: boolean): AgreementLimit | undefined {
    return this.activeLimits.find(
      limit => limit.agreementType === agreementType && limit.leader === leader,
    );
  }

  displayValue(limit: AgreementLimit | undefined, field: LimitFieldOption): string {
    if (!limit) return '';
    const rawValue = (limit as any)[field.key];
    return field.kind === 'percentage'
      ? limit.toDisplayPercentage(rawValue)
      : limit.toDisplayCurrency(rawValue);
  }

  fieldValue(agreementType: AgreementType, leader: boolean, fieldKey: string): any {
    const limit = this.getLimit(agreementType, leader) as any;
    return limit ? limit[fieldKey] : null;
  }

  onFieldInput(
    agreementType: AgreementType,
    leader: boolean,
    fieldKey: string,
    value: any,
  ): void {
    const limit = this.getLimit(agreementType, leader) as any;
    if (!limit) return;
    limit[fieldKey] = value;
  }

  setKey(agreementType: AgreementType, leader: boolean): string {
    return `${agreementType}-${leader}`;
  }

  crossFieldErrorMessages(
    agreementType: AgreementType,
    leader: boolean,
    fieldKey: string,
  ): string[] {
    if (!MIN_MAX_FIELD_KEYS.includes(fieldKey)) return [];
    const message = this.crossFieldErrors[this.setKey(agreementType, leader)];
    return message ? [message] : [];
  }

  validateMinMaxPerSet(draft: AgreementLimit[]): boolean {
    this.crossFieldErrors = {};
    let isValid = true;
    draft.forEach((limit) => {
      if (limit.duczFoodMinimumValue <= limit.duczFoodMaximumValue) return;
      isValid = false;
      const agreementTypeLabel = this.agreementTypes.find(
        option => option.key === limit.agreementType,
      );
      const cargoSectionLabel = this.cargoSections.find(
        option => option.leader === limit.leader,
      );
      this.crossFieldErrors[this.setKey(limit.agreementType, limit.leader)] = `${
        agreementTypeLabel ? agreementTypeLabel.label : ''
      } — ${cargoSectionLabel ? cargoSectionLabel.label : ''}: mínimo não pode ser maior que o máximo`;
    });
    return isValid;
  }

  requestHistory(): void {
    this.showHistoryDrawer = true;
  }

  requestEdit(): void {
    this.showEditWarning = true;
  }

  confirmEdit(): void {
    this.draftLimits = this.originalLimits.map(limit => limit.clone());
    this.editMode = true;
    this.showEditWarning = false;
  }

  requestSave(): void {
    const { form }: any = this.$refs;
    const isFormValid = form.validate();
    const isCrossFieldValid = this.validateMinMaxPerSet(this.draftLimits);
    if (!isFormValid || !isCrossFieldValid) return;
    this.showSaveConfirmation = true;
  }

  confirmSave(): Promise<void> {
    this.showSaveConfirmation = false;
    this.saving = true;
    return AgreementLimitService.updateAll(this.draftLimits)
      .then(({ data }: any) => {
        this.originalLimits = data ? AgreementLimit.ofList(data) : this.draftLimits;
        this.editMode = false;
        VbSnackbarService.showSuccess('Limites de remuneração salvos com sucesso!');
      })
      .catch((error: any) => {
        VbSnackbarService.handleHttpError(error);
      })
      .finally(() => {
        this.saving = false;
      });
  }

  requestCancel(): void {
    this.showCancelConfirmation = true;
  }

  confirmCancel(): void {
    this.showCancelConfirmation = false;
    this.draftLimits = [];
    this.crossFieldErrors = {};
    this.editMode = false;
  }

  loadLimits(): void {
    this.loading = true;
    AgreementLimitService.findAll()
      .then(({ data }: any) => {
        this.originalLimits = AgreementLimit.ofList(data);
      })
      .catch((error: any) => {
        if (error && error.response && error.response.status === 403) {
          this.accessDenied = true;
        } else {
          VbSnackbarService.handleHttpError(error);
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  created(): void {
    this.loadLimits();
  }
}
